import {
  DeleteOutlined,
  DownloadOutlined,
  DragOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FolderFilled,
  GlobalOutlined,
  ShareAltOutlined,
} from '@ant-design/icons'
import { Empty, Input, message, Popconfirm, Space, Tag, Tooltip } from 'antd'
import { observer } from 'mobx-react'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { blue, redPrimary } from '../../color'
import AppPagination from '../../components/Common/AppPagination'
import TableComponent from '../../components/Common/TableComponent'
import FileViewPopup from '../../components/FileViewPopup'
import authenticationStore from '../../stores/authenticationStore'
import fileStore from '../../stores/fileStore'
import selectPeopleStore from '../../stores/selectPeopleStore'
import { ACL_ACTION_TYPE } from '../../constants'
import AccessControlAction from '../../components/AccessControlAction'

const MyDocumentTableView = ({ store, onMove, onShare, fetchData }) => {
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [previewFile, setPreviewFile] = useState(null)

  useEffect(() => {
    if (
      !selectPeopleStore.departmentWithUserList ||
      selectPeopleStore.departmentWithUserList.length === 0
    ) {
      selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
      selectPeopleStore.getDepartmentWithUsers()
    }
  }, [])

  const formatFullName = name => {
    if (!name) return name
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const userMap = useMemo(() => {
    const map = new Map()
    if (selectPeopleStore.departmentWithUserList) {
      selectPeopleStore.departmentWithUserList.forEach(dept => {
        if (dept.users) {
          dept.users.forEach(u => {
            map.set(
              u.user_name,
              formatFullName(u.full_name || u.name_uppercase || u.user_name)
            )
          })
        }
      })
    }
    return map
  }, [selectPeopleStore.departmentWithUserList])

  const handleRename = async record => {
    if (!editName || editName === record.name) {
      setEditingId(null)
      return
    }
    try {
      await store.renameDocument(record.id, { name: editName })
      message.success('Đổi tên thành công')
      setEditingId(null)
      fetchData()
    } catch (error) {
      message.error('Lỗi khi đổi tên')
    }
  }

  const handleDelete = async id => {
    try {
      await store.deleteDocument(id)
      message.success('Xóa thành công')
      fetchData()
    } catch (error) {
      message.error('Lỗi khi xóa')
    }
  }

  const handleTogglePublish = async record => {
    try {
      await store.publishDocument(record.id, {
        isPublished: !record.isPublished,
      })
      message.success(
        record.isPublished ? 'Hủy công khai thành công' : 'Công khai thành công'
      )
      fetchData()
    } catch (error) {
      message.error('Lỗi khi cập nhật trạng thái công khai')
    }
  }

  const renderActions = record => {
    if (record.isSeparator) return null

    const isSharedWithMe =
      store.currentParentId === '' &&
      store.myDocumentList.every(doc => doc.id !== record.id)
    if (isSharedWithMe) {
      return null
    }

    return (
      <Space size="middle">
        {record.type !== 'folder' && (
          <>
            <Tooltip title="Xem trước">
              <EyeOutlined
                style={{ color: '#8c8c8c', cursor: 'pointer', fontSize: 16 }}
                onClick={() =>
                  setPreviewFile({
                    file_id:
                      record.gridFsFileId ||
                      record.fileId ||
                      record.file_id ||
                      record.id,
                    file_name: record.name,
                  })
                }
              />
            </Tooltip>
            <Tooltip title="Tải xuống">
              <DownloadOutlined
                style={{ color: '#8c8c8c', cursor: 'pointer', fontSize: 16 }}
                onClick={() =>
                  fileStore.handleDownloadFile(
                    record.gridFsFileId ||
                      record.fileId ||
                      record.file_id ||
                      record.id,
                    record.name
                  )
                }
              />
            </Tooltip>
          </>
        )}
        <AccessControlAction aclActionType={ACL_ACTION_TYPE.share__MY_DOCUMENT}>
          <Tooltip title="Chia sẻ">
            <ShareAltOutlined
              style={{ color: '#8c8c8c', cursor: 'pointer', fontSize: 16 }}
              onClick={() => onShare(record)}
            />
          </Tooltip>
        </AccessControlAction>

        <AccessControlAction aclActionType={ACL_ACTION_TYPE.move__MY_DOCUMENT}>
          <Tooltip title="Di chuyển">
            <DragOutlined
              style={{ color: '#8c8c8c', cursor: 'grab', fontSize: 16 }}
              onClick={() => onMove(record)}
              draggable
              onDragStart={e => {
                e.dataTransfer.setData('text/plain', record.id)
                e.dataTransfer.effectAllowed = 'move'
              }}
            />
          </Tooltip>
        </AccessControlAction>

        <AccessControlAction aclActionType={ACL_ACTION_TYPE.edit__MY_DOCUMENT}>
          <Tooltip title={record.isPublished ? 'Hủy công khai' : 'Công khai'}>
            <GlobalOutlined
              style={{
                color: record.isPublished ? blue : '#8c8c8c',
                cursor: 'pointer',
                fontSize: 16,
              }}
              onClick={() => handleTogglePublish(record)}
            />
          </Tooltip>
        </AccessControlAction>

        <AccessControlAction aclActionType={ACL_ACTION_TYPE.delete__MY_DOCUMENT}>
          <Popconfirm
            title="Bạn có chắc muốn xóa?"
            onConfirm={() => handleDelete(record.id)}>
            <Tooltip title="Xóa">
              <DeleteOutlined
                style={{ color: redPrimary, cursor: 'pointer', fontSize: 16 }}
              />
            </Tooltip>
          </Popconfirm>
        </AccessControlAction>
      </Space>
    )
  }

  const getFileIcon = (type, name) => {
    if (type === 'folder')
      return (
        <FolderFilled
          style={{ color: '#faad14', fontSize: 18, marginRight: 8 }}
        />
      )
    if (name?.endsWith('.xlsx') || name?.endsWith('.xls'))
      return (
        <FileExcelOutlined
          style={{ color: '#52c41a', fontSize: 18, marginRight: 8 }}
        />
      )
    if (name?.endsWith('.doc') || name?.endsWith('.docx'))
      return (
        <FileWordOutlined
          style={{ color: '#1890ff', fontSize: 18, marginRight: 8 }}
        />
      )
    if (name?.endsWith('.pdf'))
      return (
        <FilePdfOutlined
          style={{ color: '#ff4d4f', fontSize: 18, marginRight: 8 }}
        />
      )
    return (
      <FileTextOutlined
        style={{ color: '#8c8c8c', fontSize: 18, marginRight: 8 }}
      />
    )
  }

  const columns = [
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        if (record.isSeparator) {
          return {
            children: (
              <div
                style={{
                  background: '#f5f5f5',
                  color: '#8c8c8c',
                  padding: '8px 16px',
                  fontWeight: 'bold',
                }}>
                {text}
              </div>
            ),
            props: { colSpan: 6, style: { padding: 0 } },
          }
        }

        const isEditing = editingId === record.id
        const isFolder = record.type === 'folder'

        if (isEditing) {
          return {
            children: (
              <Input
                autoFocus
                value={editName}
                onChange={e => setEditName(e.target.value)}
                onPressEnter={() => handleRename(record)}
                onBlur={() => handleRename(record)}
              />
            ),
            props: { colSpan: 1 },
          }
        }

        return {
          children: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: isFolder ? 'pointer' : 'default',
              }}
              onDoubleClick={() => {
                setEditingId(record.id)
                setEditName(record.name)
              }}>
              {getFileIcon(record.type, record.name)}
              <span
                style={{ color: '#333' }}
                onClick={() => {
                  if (isFolder) store.setCurrentParentId(record.id)
                  else
                    setPreviewFile({
                      file_id:
                        record.gridFsFileId ||
                        record.fileId ||
                        record.file_id ||
                        record.id,
                      file_name: record.name,
                    })
                }}>
                {text}
              </span>
            </div>
          ),
          props: { colSpan: 1 },
        }
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      render: (date, record) => {
        if (record.isSeparator) return { props: { colSpan: 0 } }
        return date ? moment(date).format('HH:mm - DD/MM/YYYY') : ''
      },
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      align: 'center',
      render: (date, record) => {
        if (record.isSeparator) return { props: { colSpan: 0 } }
        return date ? moment(date).format('HH:mm - DD/MM/YYYY') : ''
      },
    },
    {
      title: 'Chia sẻ với',
      key: 'sharedWith',
      align: 'center',
      render: (_, record) => {
        if (record.isSeparator) return { props: { colSpan: 0 } }
        if (
          record.sharedWith &&
          Array.isArray(record.sharedWith) &&
          record.sharedWith.length > 0
        ) {
          return (
            <Space size={[0, 4]} wrap>
              {record.sharedWith.map((user, index) => {
                const username = user.username || user.id || user
                const fullName =
                  userMap.get(username) ||
                  formatFullName(user.fullName || username)
                return (
                  <Tag
                    color="blue"
                    style={{ borderRadius: 4, margin: 0 }}
                    key={index}>
                    {fullName}
                  </Tag>
                )
              })}
            </Space>
          )
        }
        return <span style={{ color: '#bfbfbf' }}>—</span>
      },
    },
    {
      title: 'Chia sẻ bởi',
      dataIndex: 'ownerUser',
      key: 'ownerUser',
      align: 'center',
      render: (ownerUser, record) => {
        if (record.isSeparator) return { props: { colSpan: 0 } }
        const fullName = userMap.get(ownerUser) || formatFullName(ownerUser)
        return ownerUser ? (
          <Tag color="blue" style={{ borderRadius: 4, margin: 0 }}>
            {fullName}
          </Tag>
        ) : (
          <span style={{ color: '#bfbfbf' }}>—</span>
        )
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 250,
      align: 'center',
      render: (_, record) => {
        if (record.isSeparator) return { props: { colSpan: 0 } }
        return renderActions(record)
      },
    },
  ]

  const dataSource = [...store.myDocumentList]
  if (store.sharedDocumentList && store.sharedDocumentList.length > 0) {
    dataSource.push({
      id: 'separator_shared',
      isSeparator: true,
      name: 'ĐƯỢC CHIA SẺ VỚI TÔI',
    })
    dataSource.push(...store.sharedDocumentList)
  }

  const onRow = record => {
    if (record.isSeparator || record.type !== 'folder') return {}
    return {
      onDragOver: e => {
        e.preventDefault()
      },
      onDrop: async e => {
        e.preventDefault()
        const srcId = e.dataTransfer.getData('text/plain')
        if (srcId && srcId !== record.id) {
          try {
            await store.moveDocument(srcId, record.id)
            message.success('Di chuyển thành công')
            fetchData()
          } catch (error) {
            message.error('Lỗi khi di chuyển')
          }
        }
      },
    }
  }

  return (
    <>
      <TableComponent
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        onRow={onRow}
        pagination={false}
        scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'Không có dữ liệu'}
            />
          ),
        }}
      />

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <AppPagination
          page={store.queryParams.page + 1}
          size={store.queryParams.size}
          totalCount={store.totalCount + store.sharedTotalCount}
          onChange={(page, size) => {
            store.setQueryParams({ page: page - 1, size })
            fetchData()
          }}
        />
      </div>

      <FileViewPopup
        file={previewFile || {}}
        isVisibleFileViewPopup={!!previewFile}
        handleCloseFileViewPopup={() => setPreviewFile(null)}
      />
    </>
  )
}

export default observer(MyDocumentTableView)
